import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export type ApprovalGranularity = "week" | "month";

export interface ApprovalTypeStat {
    /** `${entity}:${type}` — Event and Person both have a "linkedin" type. */
    key: string;
    entity: "event" | "person";
    type: string;
    /** Approved in the base right now, whatever the approval date. */
    total: number;
    manual: number;
    auto: number;
    /** Approved by a migration copying establishment data (reviewedBy "migration"): in `total` only. */
    imported: number;
    /** Approved without a reviewedAt (old migrations/imports): in `total`, in no period. */
    undated: number;
    /** Dates rebuilt after the fact (reviewedAtRecoveredFrom) — in the series, but reconstructed. */
    recovered: number;
    lastApprovedAt: string | null;
    /** One cell per entry of `periods`, by approval date. */
    series: { manual: number; auto: number }[];
}

export interface ApprovalKpis {
    granularity: ApprovalGranularity;
    generatedAt: string;
    /** Oldest first; the last one is the current, partial period. */
    periods: { start: string; end: string }[];
    base: { events: number; people: number };
    types: ApprovalTypeStat[];
}

export const getApprovalKpis = async (granularity: ApprovalGranularity, periods: number): Promise<ApprovalKpis> => {
    const res = await axios.get(`${API_URL}/admin/enrichment-kpis/approvals`, {
        params: { granularity, periods },
        headers: authHeaders(),
    });
    return res.data;
};

// ─── Funnel & coverage ────────────────────────────────────────────────────────

export interface FunnelTypeStat {
    key: string;
    entity: "event" | "person";
    type: string;
    total: number;
    /** Never searched yet (or re-queued). */
    pending: number;
    /** Being searched, or waiting for the provider's answer. */
    inProgress: number;
    /** Found, waiting for a review (manual or auto-approve). */
    toReview: number;
    approved: number;
    /** Searched, nothing usable (retry / error). */
    noResult: number;
    rejected: number;
    closed: number;
    toReviewByLevel: Record<string, number>;
}

export interface FunnelKpis {
    types: FunnelTypeStat[];
    computedAt: string;
    stale: boolean;
}

export interface CoverageKpis {
    counts: Record<string, number> & { total: number };
    bySource: { field: string; source: string; count: number }[];
    computedAt: string;
    stale: boolean;
}

export interface NearCertainItem {
    eventId: string;
    companyName: string;
    type: string;
    kind: "website_crawl" | "social" | "restaurant_name";
    value: any;
    reason: string;
}

export const getFunnelKpis = async (refresh = false): Promise<FunnelKpis> => {
    const res = await axios.get(`${API_URL}/admin/enrichment-kpis/funnel`, { params: refresh ? { refresh: 1 } : {}, headers: authHeaders() });
    return res.data;
};

export const getCoverageKpis = async (refresh = false): Promise<CoverageKpis> => {
    const res = await axios.get(`${API_URL}/admin/enrichment-kpis/coverage`, { params: refresh ? { refresh: 1 } : {}, headers: authHeaders() });
    return res.data;
};

export const getNearCertainPreview = async (kind?: string, limit = 50): Promise<{ enabled: boolean; items: NearCertainItem[] }> => {
    const res = await axios.get(`${API_URL}/admin/enrichment-kpis/near-certain-preview`, { params: { kind, limit }, headers: authHeaders() });
    return res.data;
};
